import { PathsV1VehicleSearchPostParametersQueryExtend } from "@/api-schema-enums";
import { useVehicleSearchQuery, VehicleSearchBody } from "@/common/api/vehicle/use-vehicle-search.query";
import { useComboboxPagination } from "@/common/hooks/use-combobox-pagination";
import { BaseFilterProps, Filter, FilterData, FilterPaginationState } from "../_types";
import { FilterComponent } from "../base-filter";

const FILTER_PAGE_SIZE = 50;

function useVehicleFilterData(body: VehicleSearchBody): { data: FilterData; pagination: FilterPaginationState } {
  const { debouncedSearchTerm, pageSize, createPaginationState } = useComboboxPagination({
    initialPageSize: FILTER_PAGE_SIZE,
  });

  const {
    data: vehicles,
    isPending: isLoading,
    error,
  } = useVehicleSearchQuery({
    options: {
      parameters: {
        pageSize,
        search: debouncedSearchTerm || undefined,
        extend: [
          PathsV1VehicleSearchPostParametersQueryExtend.ModelGeneration,
          PathsV1VehicleSearchPostParametersQueryExtend.ModelGeneration_ModelFamily,
          PathsV1VehicleSearchPostParametersQueryExtend.ModelGeneration_ModelFamily_Brand,
        ],
      },
      body,
    },
  });

  const optionMap = new Map<string, string>();

  for (const vehicle of vehicles?.data ?? []) {
    const labelParts = [
      vehicle.modelGeneration?.modelFamily?.brand?.name,
      vehicle.modelGeneration?.name,
      vehicle.vin,
    ].filter(Boolean);
    const label = labelParts.join(" · ") || vehicle.vin || "";
    const vehicleUuid = vehicle.uuid ?? "";

    if (!vehicleUuid || !label || optionMap.has(vehicleUuid)) {
      continue;
    }

    optionMap.set(vehicleUuid, label);
  }

  const options = Array.from(optionMap, ([id, name]) => ({ id, name }));

  const loadedCount = vehicles?.data?.length ?? 0;
  const total = vehicles?.meta?.total ?? 0;
  const pagination = createPaginationState(total, loadedCount, isLoading);

  return {
    data: {
      options,
      isLoading,
      error,
    },
    pagination,
  };
}

export class VehicleFilter extends Filter<VehicleSearchBody, unknown> {
  readonly id: string = "vehicles";
  readonly labelSingular = "common:Vehicle";
  readonly labelPlural = "common:Vehicles";
  readonly placeholder = "common:SelectVehicle";
  readonly searchPlaceholder = "common:SearchVehicle";
  readonly emptyText = "fit:NoVehicleFound";

  constructor({ id, body = {} }: { id?: string; body?: Partial<VehicleSearchBody> } = {}) {
    super();
    if (id) this.id = id;
    this.body = body as VehicleSearchBody;
  }

  getData(): FilterData {
    return { options: [], isLoading: false, error: null };
  }

  render(props: BaseFilterProps) {
    return <VehicleFilterComponent filter={this} {...props} />;
  }
}

function VehicleFilterComponent({ filter, ...props }: { filter: VehicleFilter } & BaseFilterProps) {
  const { data, pagination } = useVehicleFilterData(filter.body);

  const filterWithData = Object.create(filter);
  filterWithData.getData = () => data;

  return <FilterComponent filter={filterWithData} pagination={pagination} {...props} />;
}
